// WebProvider.js
import { Client } from "@stomp/stompjs"
import axios from "axios"
import React, {
	createContext,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { useToasts } from "react-toast-notifications"
import { Socket } from "socket.io"
import { io } from "socket.io-client"
import {
	logOut,
	setAddFilter,
	setNeedUserLogin,
	setSocketConnectionStatus,
	setToken,
	setUpdateCompanySetor,
	setUpdateFilters
} from "../Reducers/default-reducers/globalConfigReducer"
import { endPointNotification, endPointRabbit } from "../models/constantes"; // <== endPointNotification adicionado
import { routesConfig } from "../models/routesConfig"

import { useNavigate } from "react-router-dom"

import { store, persistor } from "../store"
// Importa nosso serviço do socket de notificação
import { setGlobalError } from "../Reducers/default-reducers/globalErrorReducer"
import { createNotificationSocket } from "../Services/default/NotificationSGPService"
import LoginModal from '../Components/default-components/User/Login/LoginModal';

/**
 * @callback actionFunction
 * @param {object} action redux action pattern
 * @param {string} action.type redux action pattern
 * @param {object} action.payload redux action pattern
 * @param {function} dispatch dispatch ser utilizado caso o componente nao tenha acesso ao useDispatch
 */

/**
 *
 * @param {string} context contexto utilizado para identificar se o componente precisa ser atualizado
 * @param {actionFunction} refresher funcao de callback chamada para executar a logica no escopo do componente que esta chamando a shiftMessages
 */
export function MatchingObject(
	context,
	location = "*",
	refresher,
	persist = false
) {
	this.context = context
	this.payload = null
	this.location = location
	this.refresher = refresher
	this.persist = persist
}

const WebProvider = createContext(null)
export { WebProvider }

export default function WebProviderComponent({ children }) {
	const dispatch = useDispatch()
	const toast = useToasts()
	const rawNavigate = useNavigate()
	const context = useSelector((state) => state.global.context)
	const { userId, companyId, userName, company } = useSelector((state) => state.global)
	const matchingObjectsRef = useRef([]);
	const eventRef = useRef(null);
	const wsEvent = eventRef.current = eventRef.current || new EventTarget();
	const [matchingObjects, setMatchingObjects] = useState([]);

	/**
	 * @type {[Socket, React.Dispatch<Socket>]}
	 */
	const [socket, setSocket] = useState(null)

	// Novo state para o socket de notificações
	const [notificationSocket, setNotificationSocket] = useState(null)
	const { needUserLogin } = store.getState().global
	const companieSetorRefresher = useCallback((setor) => {
		dispatch(setUpdateCompanySetor(setor))
	}, [companyId])
	const userRefresher = useCallback((actionForm) => {
		switch (actionForm.action) {
			case "updateApp":
				if (actionForm.disconnect) {
					handleLogout()
				} else {
					window.alert(
						"Você será desconectado em 60 segundos para uma atualização no sistema."
					)
				}
				break
			case "addFilterPref":
				dispatch(setAddFilter(actionForm.filterPref))
				break
			case "updateFilterPref":
				dispatch(setUpdateFilters(actionForm.filterPref))
			default:
				break
		}
	}, [userId])

	// Conectar sockets quando usuário estiver disponível (login ou rehidratação no refresh)
	useEffect(() => {
		if (userName && company) {
			connectSocket(userName, company)
			connectNotificationSocket(userName, company)
		}
	}, [userName, company])

	useEffect(() => {
		if (userId && companyId) {
			const setorMatchingObject = new MatchingObject(
				`companie${companyId}setor`,
				"*",
				companieSetorRefresher,
				true
			)
			const userMatchingObject = new MatchingObject(
				`user${userId}`,
				"*",
				userRefresher,
				true
			)
			subscribe(setorMatchingObject)
			subscribe(userMatchingObject)
		}
	}, [userId, companyId])

	useEffect(() => {
		if (matchingObjects.length == 0) return
		let cleanedMos = matchingObjects.filter(
			(mo) => mo.context == context || mo.persist
		)
		console.log(
			`Limpou : ${matchingObjects.length - cleanedMos.length
			} Matching Objects`
		)
		updateMatchingObjects(cleanedMos)
	}, [context])

	async function handleLogout() {
		if (socket) {
			socket.disconnect()
			setSocket(null)
		}

		// Se existir notificação socket, também desconectamos
		if (notificationSocket) {
			notificationSocket.disconnect()
			setNotificationSocket(null)
		}

		// Purge persisted storage via redux-persist to ensure no stale payload is
		// rehydrated later. Avoid direct localStorage.clear() which may be unsafe.
		try {
			if (persistor && typeof persistor.purge === 'function') {
				// purge returns a promise in redux-persist
				await persistor.purge()
				// small delay can help avoid race with other tasks
			} else if (typeof window !== 'undefined') {
				// fallback: remove the specific key used by redux-persist
				try { window.localStorage.removeItem('persist:root') } catch (e) { }
			}
		} catch (e) {
			// ignore purge errors but continue with logout
			// eslint-disable-next-line no-console
			console.warn('[handleLogout] persistor.purge() failed', e)
		}

		// Clear in-memory redux state and update auth slice
		dispatch(logOut())
		dispatch({ type: "eraseStore" })

		// Finally navigate to login
		rawNavigate("/Login")
	}
	function updateMatchingObjects(newList) {
		matchingObjectsRef.current = newList;
		setMatchingObjects(newList);
	}

	// Função que conecta o socket PRINCIPAL (já existente)
	const connectSocket = (userName, company) => {
		if (socket && socket.connected) {
			dispatch(setSocketConnectionStatus(true))
			return
		}
		if (userName && company) {
			let newSocket = socketClient(
				endPointRabbit,
				userName,
				company,
				onMessageReceive
			)
			setSocket(newSocket)
		}
	}

	// Função NOVA para conectar o socket de NOTIFICAÇÕES
	const connectNotificationSocket = (userName, company) => {
		// Evita recriar caso já esteja conectado
		if (notificationSocket && notificationSocket.connected) {
			console.log("[Notification] Já está conectado.")
			return
		}
		if (userName && company) {
			const token = store.getState().global?.token
			const newNotificationSocket = createNotificationSocket(
				endPointNotification,
				token,
				userName,
				company,
				onNotificationMessage
			)
			if (newNotificationSocket) setNotificationSocket(newNotificationSocket)
		}
	}

	// Callback para mensagens de notificação (caso você queira manipular algo extra)
	const onNotificationMessage = (notification) => {
		// Aqui você pode exibir um toast ou despachar algo no Redux
		if (toast?.addToast) {
			toast.addToast(`[Notificação] ${notification.message || ""}`, {
				appearance: "info",
				autoDismiss: true,
			})
		}
		// Se quiser despachar uma action Redux, algo do tipo:
		// store.dispatch({ type: "ADD_NOTIFICATION", payload: notification })
	}

	const setRestApi = (context, baseEndPoint) => {
		let endPointToConfig = null
		if (baseEndPoint) {
			endPointToConfig = baseEndPoint
		} else {
			routesConfig.forEach((rC) =>
				rC.routes.forEach((route) => {
					if (route.configuration.context === context) {
						endPointToConfig = route.configuration.endPoint
					}
				})
			)
		}

		const http = axios.create({
			baseURL: endPointToConfig,
		})

		http.interceptors.response.use(
			res => {
				if (
					res.config.method !== "get" &&
					res.config.method !== "patch"
				) {
					toast.addToast("Dados processados com sucesso", {
						autoDismiss: true,
						duration: 2000,
						appearance: "success",
					})
				}
				if (res.data?.newToken) dispatch(setToken(res.data?.newToken))
				processResponseMatchingObjects(res.data.matchingObjects)
				return res?.data?.content ? res.data.content : res.data
			},
			err => {
				const status = err.response?.status;
				const data = err.response?.data;

				// Trata 400 e 404 COMO fluxos normais
				if (status === 400 || status === 404) {
					// exibe toast de validação ou “não encontrado”
					if (status === 400 && Array.isArray(data.errors)) {
						data.errors.forEach(msg =>
							toast.addToast(msg, { appearance: 'warning', autoDismiss: true })
						);
					}
					if (status === 404) {
						toast.addToast('Recurso não encontrado.', { appearance: 'info', autoDismiss: true });
					}
					// devolve o conteúdo para a chamada seguir como se fosse sucesso
					return Promise.resolve(data);
				}

				// continua seu fluxo para outros status
				// Retry logic for 401 or specific 403 (missing token)
				if (status === 401 || (status === 403 && typeof data === 'string' && data.includes("A token is required for authentication"))) {
					dispatch(setNeedUserLogin(true));

					return new Promise((resolve, reject) => {
						const unsubscribe = store.subscribe(() => {
							const state = store.getState();
							const token = state.global.token;
							const needLogin = state.global.needUserLogin;

							if (token && !needLogin) {
								unsubscribe();
								err.config.headers['Authorization'] = `${token}`;
								http.request(err.config).then(resolve).catch(reject);
							} else if (!token && !needLogin) {
								unsubscribe();
								// Se o login foi cancelado, lança o erro global
								const customError = { message: err.message, status, data, stack: err.stack };
								dispatch(setGlobalError(customError));
								reject(customError);
							}
						});
					});
				}

				if (status === 403) {
					toast.addToast('Você não tem permissão para acessar este recurso.', { autoDismiss: true });
				}
				if (status === 500 && Array.isArray(data.errors)) {
					data.errors.forEach(errMsg =>
						toast.addToast(errMsg, { autoDismiss: true, duration: 2000 })
					);
				}

				const customError = { message: err.message, status, data, stack: err.stack };
				dispatch(setGlobalError(customError));
				return Promise.reject(customError);
			}
		);

		http.interceptors.request.use(async (config) => {
			const token = store.getState().global.token
			if (token) {
				config.headers.Authorization = `${token}`
			}
			return config
		})

		return http
	}


	const basicController = (context, baseEndPoint) => {
		let api = setRestApi(context, baseEndPoint)
		return {
			get: (path, query) => {
				let pathh = path ? path : context
				return api.get(`${pathh}${query ? "?" + query : ""}`, {
					headers: { Contexto: context },
				})
			},
			post: (path, data, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.post(`${pathh}${query ? "?" + query : ""}`, data, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			put: (path, data, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.put(`${pathh}${query ? "?" + query : ""}`, data, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			patch: (path, data, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.patch(`${pathh}${query ? "?" + query : ""}`, data, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			deleteSimple: (path, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.delete(`${pathh}${query ? "?" + query : ""}`, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			save: (path, data, extraHeaders, query) => {
				let pathh = path ? path : context
				if (data.id || data._id) {
					return api.put(
						`${pathh}/${data.id || data._id}${query ? "?" + query : ""
						}`,
						data,
						{ headers: { Contexto: context, ...extraHeaders } }
					)
				} else {
					return api.post(
						`${pathh}${query ? "?" + query : ""}`,
						data,
						{
							headers: { Contexto: context, ...extraHeaders },
						}
					)
				}
			},
			read: (path, id, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.get(`${pathh}/${id}${query ? "?" + query : ""}`, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			readAll: (path, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.get(`${pathh}${query ? "?" + query : ""}`, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			readAllwithPage: (path, page, size, extraHeaders) => {
				let pathh = path ? path : context
				return api.get(`${pathh}?page=${page}&size=${size}`, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			delete: (path, id, extraHeaders, query) => {
				let pathh = path ? path : context
				return api.delete(`${pathh}/${id}${query ? "?" + query : ""}`, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
			bulkDelete: (path, ids, extraHeaders = {}, query = "") => {
				const pathh = path || context
				// Cria o parâmetro de bulk delete: ids=1,2,3,4
				const bulkParam = `ids=${ids.join(",")}`
				// Se existir query adicional, adiciona com "&"
				const fullQuery = query ? `${query}&${bulkParam}` : bulkParam
				// Constrói a URL com a query string
				const url = `${pathh}?${fullQuery}`
				return api.delete(url, {
					headers: { Contexto: context, ...extraHeaders },
				})
			},
		}
	}
	const processResponseMatchingObjects = (matchingObjects) => {
		let mos = matchingObjects
		if (!mos) return
		if (matchingObjects && !Array.isArray(matchingObjects))
			mos = [matchingObjects]
		for (let mo of mos) {
			//manda socketType falso para que nao ocorra a renderizacao caso o usuario seja o mesmo
			onMessageReceive(mo, false)
		}
	}

	// Continua do seu jeito atual
	const onMessageReceive = (incomingMatchingObject, socketType = true) => {
		console.log("[Principal] Mensagem recebida:", incomingMatchingObject)

		if (typeof incomingMatchingObject != "object") return
		if (matchingObjectsRef.current.length > 0) {
			let filteredmO = matchingObjectsRef.current.filter(
				(mO) =>
					mO.context === incomingMatchingObject.context &&
					incomingMatchingObject.location === mO.location
			)
			try {
				//disparo os refreshers
				for (const mO of filteredmO) {
					//caso esteja vindo pelo socket atualiza apenas se o user for diferente de quem gerou a atualizacao
					if (socketType && incomingMatchingObject.userId != userId) {
						if (mO.refresher) {
							mO.refresher(
								incomingMatchingObject.payload,
								dispatch
							)
						} else {
							wsEvent.dispatchEvent(
								new CustomEvent(mO.context + mO.location, {
									detail: incomingMatchingObject.payload,
								})
							)
						}
					}
					// caso o sockettype seja false significa que a atualizacao esta vindo da response o que autoriza o disparo do refresh
					if (!socketType) {
						if (mO.refresher) {
							mO.refresher(
								incomingMatchingObject.payload,
								dispatch
							)
						} else {
							wsEvent.dispatchEvent(
								new CustomEvent(mO.context + mO.location, {
									detail: incomingMatchingObject.payload,
								})
							)
						}
					}
				}
			} catch (error) {
				console.error(
					"Falha ao processar o refresher de Mo context :  " +
					incomingMatchingObject.context
				)
			}
		}
	}

	const socketClient = (endpoint, user, company, onMessage) => {
		const token = store.getState().global.token

		let newSocket = io(endpoint, {
			extraHeaders: {
				user,
				company,
				token,
				type: "app",
			},
		})

		newSocket.on("connect", () => {
			dispatch(setSocketConnectionStatus(true))
			console.log("[Principal] Socket conectado:", newSocket.id)
		})

		newSocket.on("message", (data, ack) => {
			console.log("[Principal] Mensagem recebida:", data)
			onMessage(data)
			ack && ack({ status: "received" })
		})

		newSocket.on("connect_error", (err) => {
			dispatch(setSocketConnectionStatus(false))
			setTimeout(() => {
				newSocket.connect()
			}, 1000)
		})

		newSocket.on("disconnect", (msg) => {
			dispatch(setSocketConnectionStatus(false))
			console.log("[Principal] Socket desconectado:", msg)
		})

		newSocket.on("close", (msg) => {
			dispatch(setSocketConnectionStatus(false))
			console.log("[Principal] Socket fechado:", msg)
		})

		newSocket.on("error", (msg) => {
			dispatch(setSocketConnectionStatus(false))
			console.log("[Principal] Socket erro:", msg)
		})

		newSocket.on("expired-token", () => {
			dispatch(setNeedUserLogin(true))
		})

		return newSocket
	}

	const stompClient = (endpoint, user, company, onMessage) => {
		let stomp = null

		const onConnect = () => {
			stomp.subscribe("/", (message) => {
				// ...
			})
		}

		const onDisconnect = () => {
			stomp.deactivate()
		}

		stomp = new Client({
			brokerURL: endpoint,
			reconnectDelay: 5000,
			heartbeatIncoming: 4000,
			heartbeatOutgoing: 4000,
			onConnect,
			onDisconnect,
		})

		stomp.activate()
		return stomp
	}

	const sendMessage = (matchingObject) => {
		if (socket) {
			socket.send(JSON.stringify(matchingObject))
		}
	}

	function subscribe(matchingObject) {
		matchingObject.userId = userId
		let matchingObjects = matchingObjectsRef.current || []
		if (
			!matchingObjects.find(
				(mO) =>
					mO.context === matchingObject.context &&
					matchingObject.location === mO.location
			)
		) {
			console.log(
				"[Principal] Subscreveu " + JSON.stringify(matchingObject)
			)
			let addedMos = [...matchingObjects, matchingObject]

			updateMatchingObjects(addedMos)
		}
	}

	const unsubscribe = (matchingObject) => {
		let consumers = matchingObjectsRef.current || []
		let filteredMos = consumers.filter((c) => c.context !== matchingObject.context)
		updateMatchingObjects(filteredMos)
	}

	const subscribeEvent = (context, location, eventHandler) => {
		let eventName = context + location
		wsEvent.addEventListener(eventName, eventHandler)
		subscribe(new MatchingObject(context, location))
	}
	const unsubscribeEvent = (context, location, eventHandler) => {
		let eventName = context + location
		wsEvent.removeEventListener(eventName, eventHandler);
		unsubscribe(new MatchingObject(context, location))
	}

	const value = {
		wsEvent,
		socket,
		notificationSocket,
		handleLogout,
		sendMessage,
		unsubscribe,
		subscribe,
		setRestApi,
		basicController,
		stompClient,
		connectSocket,
		connectNotificationSocket, // expõe a função p/ reconexões manuais (se quiser)
		subscribeEvent,
		unsubscribeEvent
	}

	return (
		<WebProvider.Provider value={value}>
			<LoginModal show={needUserLogin} closeFunc={handleLogout} />
			{children}
		</WebProvider.Provider>
	)
}
